/**
 * Step 6.5 -- hanging things off a character.
 *
 * The art provides no attachment points at all: rig A has `Prop_L`/`Prop_R` and rig C
 * has neither, and nothing anywhere provides a quiver, back or hip socket. The
 * pipeline synthesises the three onto the canonical skeleton, so by the time a
 * character reaches the runtime they are ordinary bones and attaching is just
 * parenting to one.
 *
 * A weapon is parented, not skinned -- every hero weapon is an unrigged mesh, so it
 * rides its socket's world matrix. The bow is the exception: it carries its own
 * 17-joint rig, and its 104 prop clips drive that rig while the body plays a
 * bow-agnostic clip. The two run in lockstep, which is what lets one body animation
 * work with any bow.
 */
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { AnimationGroup } from '@babylonjs/core/Animations/animationGroup';
import { Matrix, Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import type { Scene } from '@babylonjs/core/scene';
import type { Skeleton } from '@babylonjs/core/Bones/skeleton';
import type { Node } from '@babylonjs/core/node';

import { loadSharedContainer } from './assetCache.ts';

import '@babylonjs/loaders/glTF/2.0';

export type SocketName = 'propLeft' | 'propRight' | 'quiver';

const SOCKET_BONES: Record<SocketName, string> = {
  propLeft: 'Prop_L',
  propRight: 'Prop_R',
  quiver: 'Quiver',
};

export interface AttachedWeapon {
  /** Root of the attached meshes; follows the socket every frame. */
  root: TransformNode;
  meshes: AbstractMesh[];
  /** The bow's own rig, when the weapon has one. */
  skeleton: Skeleton | null;
  /** Prop clips, keyed by the body clip they pair with. */
  propClips: Map<string, AnimationGroup>;
  dispose(): void;
}

export interface AttachOptions {
  socket: SocketName;
  /** URL of the weapon GLB. */
  url: string;
  /** Prop clip URLs keyed by the body clip id they accompany. */
  propClips?: Record<string, string>;
}

/**
 * Parents `node` to a skeleton bone, keeping it there as the bone animates.
 *
 * Babylon's `attachToBone` expects the mesh to follow a bone of a *rendered* mesh; a
 * plain transform node is simpler and works for anything, which matters because a
 * quiver is not a weapon and a bow brings its own hierarchy.
 */
export function followBone(scene: Scene, skeleton: Skeleton, boneName: string): TransformNode | null {
  const bone = skeleton.bones.find((entry) => entry.name === boneName);
  if (!bone) return null;

  const node = new TransformNode(`socket:${boneName}`, scene);
  const linked = bone.getTransformNode();

  if (linked) {
    // The pipeline synthesised the sockets as real bones, so the loader gave each a
    // transform node and simple parenting is enough.
    node.parent = linked;
    return node;
  }

  // Fallback: drive it from the bone matrix each frame.
  const observer = scene.onBeforeRenderObservable.add(() => {
    const matrix = bone.getFinalMatrix();
    const scale = new Vector3();
    const rotation = new Quaternion();
    const translation = new Vector3();
    matrix.decompose(scale, rotation, translation);
    node.position.copyFrom(translation);
    node.rotationQuaternion = rotation;
    node.scaling.copyFrom(scale);
  });
  node.onDisposeObservable.addOnce(() => scene.onBeforeRenderObservable.remove(observer));
  return node;
}

export async function attachWeapon(
  scene: Scene,
  skeleton: Skeleton,
  options: AttachOptions,
): Promise<AttachedWeapon | null> {
  const boneName = SOCKET_BONES[options.socket];
  const socket = followBone(scene, skeleton, boneName);
  if (!socket) {
    console.warn(`[character] no socket bone "${boneName}" on this skeleton`);
    return null;
  }

  // Parsed once per scene, copied per character (step 7.4): four archers share one
  // parse of the bow and of each of its prop clips.
  const container = await loadSharedContainer(scene, options.url);
  const copy = container.instantiateModelsToScene((name) => name, false, {
    doNotInstantiate: true,
  });

  const owned = copy.rootNodes.flatMap((node) => [node, ...node.getDescendants(false)]);
  const meshes = owned.filter(
    (node): node is AbstractMesh => ((node as AbstractMesh).getTotalVertices?.() ?? 0) > 0,
  );
  const weaponSkeleton = copy.skeletons[0] ?? null;

  // The loader wraps every glTF in a `__root__` carrying its handedness conversion;
  // re-parenting that node keeps the conversion and moves the whole weapon at once.
  const root = (copy.rootNodes[0] as TransformNode | undefined) ?? null;
  if (root) root.parent = socket;
  else for (const mesh of meshes) mesh.parent = socket;

  // This weapon's own nodes, by name. A prop clip was authored against the weapon's
  // skeleton, but against the *shared* copy of it -- so it is rebound here exactly as
  // a body clip is rebound to a character.
  const targets = new Map<string, Node>();
  for (const node of owned) if (node.name) targets.set(node.name, node);

  const propClips = new Map<string, AnimationGroup>();
  for (const [bodyClip, url] of Object.entries(options.propClips ?? {})) {
    const clipContainer = await loadSharedContainer(scene, url, { hold: 'pin' });
    const source = clipContainer.animationGroups[0];
    if (!source) continue;

    const group = new AnimationGroup(`${bodyClip}:prop`, scene);
    for (const targeted of source.targetedAnimations) {
      const node = targets.get((targeted.target as { name?: string }).name ?? '');
      if (node) group.addTargetedAnimation(targeted.animation, node);
    }
    if (group.targetedAnimations.length === 0) {
      group.dispose();
      continue;
    }
    group.normalize(source.from, source.to);
    group.stop();
    propClips.set(bodyClip, group);
  }

  return {
    root: socket,
    meshes,
    skeleton: weaponSkeleton,
    propClips,
    dispose() {
      for (const group of propClips.values()) group.dispose();
      for (const mesh of meshes) mesh.dispose();
      weaponSkeleton?.dispose();
      socket.dispose(false, true);
    },
  };
}

/** Bone-space offset of one node from another, for measuring sockets at runtime. */
export function relativeTransform(from: Node, to: Node): Matrix {
  const inverse = new Matrix();
  from.getWorldMatrix().invertToRef(inverse);
  return to.getWorldMatrix().multiply(inverse);
}
